import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import { firebaseSignOut } from '../lib/firebase';

export type Organization = {
  id: string;
  name: string;
  brand_color: string;
  brand_logo_url: string | null;
  auth_policy: string;
  sms_otp_enabled: boolean;
  role?: string;
  permissions?: string[];
};

export type User = { id: string; email: string; name: string; mfa_enabled: boolean };

type SessionValue = {
  user: User | null;
  organizations: Organization[];
  org: Organization | null;
  orgId: string | null;
  loading: boolean;
  /** The deployment's environment, from /api/health. */
  environment: string | null;
  /** Whether outbound mail is simulated rather than sent. The development
   *  mailbox exists only because nothing was really sent, so this — not the
   *  name of the environment — is what decides whether to offer it. A staging
   *  deployment with a real provider sends real mail, and a link promising to
   *  show it would be a lie that also exposes signing tokens. */
  mailSimulated: boolean;
  can: (permission: string) => boolean;
  refresh: () => Promise<void>;
  selectOrg: (id: string) => void;
  signOut: () => Promise<void>;
};

const SessionContext = createContext<SessionValue | null>(null);
const ORG_KEY = 'esign.org';

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState<string | null>(() => localStorage.getItem(ORG_KEY));
  const [loading, setLoading] = useState(true);
  const [environment, setEnvironment] = useState<string | null>(null);
  const [mailSimulated, setMailSimulated] = useState(false);

  useEffect(() => {
    api.get('/health')
      .then((health) => {
        setEnvironment(health?.environment ?? null);
        setMailSimulated(Boolean(health?.email?.simulated));
      })
      .catch(() => { setEnvironment(null); setMailSimulated(false); });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get('/auth/me');
      setUser(data.user);
      setOrganizations(data.organizations || []);
      setOrgId((current) => {
        const ids = (data.organizations || []).map((o: Organization) => o.id);
        if (current && ids.includes(current)) return current;
        return ids[0] || null;
      });
    } catch {
      setUser(null);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (orgId) localStorage.setItem(ORG_KEY, orgId);
  }, [orgId]);

  const signOut = useCallback(async () => {
    // Clear both sides: our session cookie and any Firebase credential held by
    // the SDK, so the next visit really does start signed out.
    await Promise.all([
      api.post('/auth/logout').catch(() => undefined),
      firebaseSignOut().catch(() => undefined),
    ]);
    setUser(null);
    setOrganizations([]);
    setOrgId(null);
    localStorage.removeItem(ORG_KEY);
  }, []);

  const org = useMemo(
    () => organizations.find((o) => o.id === orgId) || organizations[0] || null,
    [organizations, orgId],
  );

  const can = useCallback(
    (permission: string) => Boolean(org?.permissions?.includes(permission)),
    [org],
  );

  const value = useMemo<SessionValue>(() => ({
    user, organizations, org, orgId: org?.id || null, loading, environment,
    mailSimulated, can, refresh, selectOrg: setOrgId, signOut,
  }), [user, organizations, org, loading, environment, mailSimulated, can,
       refresh, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}
